import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { head } from 'lodash-es';

import { CodeScannerService } from '@services';
import {
  GatewayCommissioningStore,
  gatewayPasswordRegExp,
  isValidGatewayId,
  isValidViessmannSerial,
  maxlengthOfSerial,
  minlengthOfSerial,
  minMaxlengthOfGatewayPassword,
  serialRegExp,
  GATEWAY_COMMISSIONING_DEVICE,
  TransferInstallationFormData,
  RemoteConfigStore,
} from '@store';
import { ViConfirmDialogService } from '@vi-iot/ui/index';
import { TranslateService } from '@ngx-translate/core';
import { CommissioningBrokenGatewayUrl, CommissioningTags } from '@vi-iot/client/models';

import { CodeScannerResponseType } from '../../../../shared/services/code-scanner.model';
import { GatewayForm, GatewayFormGroup } from '../transfer-installation.helper';

@Component({
  selector: 'app-gateway-form',
  templateUrl: './gateway-form.component.html',
  styleUrls: ['./gateway-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GatewayFormComponent implements OnInit, OnDestroy {
  gatewayFormGroup: FormGroup<GatewayFormGroup>;
  transferInstallationLoading: boolean;
  readonly minlengthOfSerial = minlengthOfSerial;
  readonly maxlengthOfSerial = maxlengthOfSerial;
  readonly serialRegExp = serialRegExp;

  readonly minMaxlengthOfGatewayPassword = minMaxlengthOfGatewayPassword;
  readonly gatewayPasswordRegExp = gatewayPasswordRegExp;

  readonly deviceType = GATEWAY_COMMISSIONING_DEVICE;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  @Input() formData: TransferInstallationFormData;
  @Output() gatewayDataChange: EventEmitter<GatewayForm> = new EventEmitter<GatewayForm>();

  constructor(
    protected gatewayCommissioningStore: GatewayCommissioningStore,
    protected codeScannerService: CodeScannerService,
    private cdr: ChangeDetectorRef,
    private viConfirmDialogService: ViConfirmDialogService,
    private translateService: TranslateService,
    private remoteConfigStore: RemoteConfigStore
  ) {}

  ngOnInit(): void {
    this.gatewayFormGroup = new FormGroup<GatewayFormGroup>({
      gatewaySerial: new FormControl<string>(this.formData.gateway.serial, [
        Validators.required,
        Validators.pattern(this.serialRegExp),
        Validators.minLength(this.minlengthOfSerial),
        Validators.maxLength(this.maxlengthOfSerial),
      ]),
      gatewayPassword: new FormControl<string>(this.formData.gateway.password, [
        Validators.required,
        Validators.pattern(this.gatewayPasswordRegExp),
        Validators.minLength(this.minMaxlengthOfGatewayPassword),
        Validators.maxLength(this.minMaxlengthOfGatewayPassword),
      ]),
    });
    this.serialOrPasswordChanged(this.formData.gateway.serial, this.formData.gateway.password);
    this.checkMissingCertificate();

    this.emitFormData();

    this.gatewayCommissioningStore.transferInstallationLoading$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((transferInstallationLoading) => {
        if (transferInstallationLoading) {
          this.gatewayFormGroup.disable();
        } else {
          this.gatewayFormGroup.enable();
        }
        this.transferInstallationLoading = transferInstallationLoading;
      });

    this.gatewayCommissioningStore.selectGatewayVerificationError$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isError) => {
        if (isError) {
          this.gatewayFormGroup.controls.gatewaySerial.setErrors({ backendError: true });
          this.gatewayFormGroup.controls.gatewayPassword.setErrors({ backendError: true });
          this.gatewayFormGroup.controls.gatewaySerial.markAsTouched();
          this.gatewayFormGroup.controls.gatewayPassword.markAsTouched();
        } else {
          this.gatewayFormGroup.controls.gatewaySerial.setErrors(null);
          this.gatewayFormGroup.controls.gatewayPassword.setErrors(null);
        }
      });

    this.gatewayCommissioningStore.isGatewayVerificationDataRegistered$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isRegistered) => {
        if (isRegistered) {
          this.gatewayFormGroup.controls.gatewaySerial.setErrors({ isRegistered: true });
          this.gatewayFormGroup.controls.gatewaySerial.markAsTouched();
        } else {
          this.gatewayFormGroup.controls.gatewaySerial.setErrors(null);
        }
      });

    this.gatewayFormGroup.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((value) => {
      this.serialOrPasswordChanged(value.gatewaySerial, value.gatewayPassword);

      this.emitFormData();
    });
  }

  checkMissingCertificate(): void {
    this.gatewayCommissioningStore.gatewaysTags$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((tags) => {
        const shouldOpenModal = tags?.some(
          (tag) => tag === CommissioningTags.hb2MissingCertificate
        );
        if (shouldOpenModal) {
          const dialog = this.viConfirmDialogService.open({
            title: this.translateService.instant('+REMOTE_SERVICE.WARNING'),
            content: this.translateService.instant(
              '+INSTALLATIONS.INSTALLATION.DEVICE_BROKEN_FIRMWARE'
            ),
            confirmBtn: this.translateService.instant('GENERAL.COOKIE_DISCLAIMER.BUTTON_MORE'),
            cancelBtn: this.translateService.instant('GENERAL.OK'),
            closeIcon: true,
          });
          dialog.subscribe((confirm) => {
            if (confirm) {
              this.goToUrl();
            }
          });
        }
      });
  }

  goToUrl(): void {
    let url: string;
    this.remoteConfigStore.isFlatRate$.pipe(take(1)).subscribe((flatRate) => {
      if (flatRate) {
        url = CommissioningBrokenGatewayUrl.flatRate;
      } else {
        url = CommissioningBrokenGatewayUrl.defaultUrl;
      }
    });
    const newTab = window.open(url, '_blank');
    newTab.focus();
  }

  openScanner(): void {
    this.codeScannerService
      .openCodeDialog()
      .pipe(
        filter((response) => response.type === CodeScannerResponseType.CODE_SCANNER_RESULT),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((response) => {
        this.parseQRCode(response.value);
      });
  }

  serialOrPasswordChanged(gatewaySerial: string, gatewayPassword: string): void {
    if (
      isValidViessmannSerial(gatewaySerial) &&
      isValidGatewayId(gatewayPassword) &&
      !this.transferInstallationLoading &&
      !this.gatewayFormGroup?.disabled
    ) {
      this.gatewayCommissioningStore.verifyGateway(gatewaySerial, gatewayPassword);
      this.gatewayFormGroup.patchValue({ gatewaySerial, gatewayPassword }, { emitEvent: false });
    }
    this.gatewayCommissioningStore.setFormData({
      gateway: { serial: gatewaySerial, password: gatewayPassword },
    });
  }

  private parseQRCode(code: string): void {
    const strippedCode = code.replace(/\s/g, '');
    const deviceCode = this.parseViessmannQR(strippedCode);

    if (isValidViessmannSerial(deviceCode.sn) && isValidGatewayId(deviceCode.id)) {
      this.serialOrPasswordChanged(deviceCode.sn, deviceCode.id);
      this.emitFormData();
      this.cdr.detectChanges();
    }
  }

  private parseViessmannQR(code: string): { sn: string; id: string } {
    const sn = head(new RegExp('S[0-9A-F]{16}').exec(code));
    const id = head(new RegExp('BPW:[0-9A-F]{16}').exec(code));

    return sn && id
      ? {
          sn: sn.substr(1),
          id: id.substr(4),
        }
      : {
          sn: '',
          id: '',
        };
  }

  private emitFormData(): void {
    this.gatewayDataChange.emit({
      isGatewayFormValid: this.gatewayFormGroup.valid,
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
